import { mapKeys, mapValues, set } from 'lodash'
import type { AppSpecData, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import type { Component, RefSchemas, Structure, FetchFn, Logger } from '@wix/thunderbolt-becky-types'
import type { BuilderLegacyComponentsMap } from '@wix/thunderbolt-commons'
import type { RefInfo } from '@wix/document-services-types'

export type BuilderRefInfo = Omit<RefInfo, 'isList' | 'isRefOwner' | 'isRelationalSplit' | 'dontSerialize'> & {
	list: boolean
	refOwner: boolean
}

export type BuilderRefSchemas = {
	[map: string]: {
		[dataType: string]: {
			references: Array<BuilderRefInfo>
		}
	}
}

export type CompSchemaRefResponse = {
	referencesMap: BuilderRefSchemas
}

export const getBuilderComponents = (clientSpecMap: ViewerClientSpecMap): Set<string> => {
	const builderComponents = new Set<string>()
	Object.values(clientSpecMap).forEach((app: AppSpecData) => {
		app.components?.forEach((comp) => {
			if (comp.type === 'EDITOR_REACT_COMPONENT') {
				builderComponents.add(comp.data.type)
			}
		})
	})
	return builderComponents
}

export const fixBuilderComponent = (component: Component) => {
	set(component, 'builderType', component.componentType)
	set(component, 'componentType', 'Builder')
}

export const fixBuilderLegacyComponent = (component: Component, refType: string) => {
	set(component, 'componentType', refType)
}

export const fixStructureWithBuilderComponents = (structure: Structure, builderComponents: Set<string>) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const isBuilderComponent = comp && builderComponents.has(comp.componentType)
		if (isBuilderComponent) {
			fixBuilderComponent(comp)
		}
	}
	return structure
}

export const fixStructureWithBuilderLegacyComponents = (
	structure: Structure,
	builderLegacyComponentsMap: BuilderLegacyComponentsMap
) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const refType = builderLegacyComponentsMap[comp.componentType]
		if (refType !== undefined) {
			fixBuilderLegacyComponent(comp, refType)
		}
	}

	return structure
}

export const fixMapNames = (refSchemas: RefSchemas): RefSchemas => {
	return mapKeys(refSchemas, (_value, key) => {
		switch (key) {
			case 'data':
				return 'document_data'
			case 'style':
				return 'theme_data'
			default:
				return key
		}
	})
}

export const builderRefInfoToRefInfo = (builderRefInfo: BuilderRefInfo): RefInfo => {
	const { list, refOwner, ...rest } = builderRefInfo
	return {
		...rest,
		isList: list,
		isRefOwner: refOwner,
	}
}

export const builderRefSchemasToRefSchemas = (refSchemas: BuilderRefSchemas): RefSchemas => {
	return mapValues(refSchemas, (map) => {
		return mapValues(map, (dataTypeObj) => {
			// DM sets without "references", fetch returns with them
			return (dataTypeObj.references || dataTypeObj).map(builderRefInfoToRefInfo)
		})
	})
}

export const getBuilderComponentsAppDefIds = (clientSpecMap: ViewerClientSpecMap): Array<string> =>
	Object.values(clientSpecMap)
		.filter((app: AppSpecData) => app.components?.some((comp) => comp.type === 'EDITOR_REACT_COMPONENT'))
		.map((app: AppSpecData) => app.appDefinitionId)

export const getBuilderComponentsApps = (clientSpecMap: ViewerClientSpecMap): Array<AppSpecData> =>
	Object.values(clientSpecMap)
		.filter((app: AppSpecData) => app.components?.some((comp) => comp.type === 'EDITOR_REACT_COMPONENT'))

const isValidUrl = (appDefId: string, logger?: Logger, url?: string): boolean => {
	try {
		if (!url) {
			throw new Error('Undefined url')
		}
		new URL(url)
		return true
	} catch (error) {
		logger?.warn(`app ${appDefId} has invalid schemaRefUrl`, { appDefId, url, error })
		return false
	}
}

function extractSchemaRefsUrls(builderComponentsAppDefIds: Array<string>, clientSpecMap: ViewerClientSpecMap, logger?: Logger): Record<string, Array<string>> {
	return Object.values(clientSpecMap)
		.filter((app: AppSpecData) => builderComponentsAppDefIds.includes(app.appDefinitionId))
		.reduce((acc, app) => {
			acc[app.appDefinitionId] = app.components
				?.filter((comp) => comp.type === 'EDITOR_REACT_COMPONENT' && isValidUrl(app.appDefinitionId, logger, comp.data.resources.schemaRefsUrl))
				.map((comp) => comp.data.resources.schemaRefsUrl!) || []
			return acc
		}, {} as Record<string, Array<string>>)
}

export const mergeComponentRefSchemas = (responses: Array<CompSchemaRefResponse>): BuilderRefSchemas => {
	const result: BuilderRefSchemas = {}
	for (const response of responses) {
		const schema = response.referencesMap
		for (const [mapName, mapContent] of Object.entries(schema)) {
			if (!result[mapName]) {
				result[mapName] = {}
			}
			result[mapName] = { ...result[mapName], ...mapContent }
		}
	}
	return result
}

interface FetchResult {
	success: boolean
	compSchemaRefResponse?: CompSchemaRefResponse
}
export const fetchSchemaRefUrl = async (url: string, fetchFn: FetchFn, appDefId: string, logger?: Logger): Promise<FetchResult> => {
	try {
		const response = await fetchFn(url)
		if (response.ok) {
			const compSchemaRefResponse = await response.json()
			return { success: true, compSchemaRefResponse }
		}
		throw new Error(`HTTP error ${response.status} - ${response.statusText}`)
	} catch (error) {
		logger?.warn(`app ${appDefId} has failing schemaRefUrl`, { appDefId, url, error })
		return { success: false }
	}
}

export const getRefsForAppDefId = async (appDefId: string, urls: Array<string>, fetchFn: FetchFn, logger?: Logger): Promise<RefSchemas> => {
	const fetchResults = await Promise.all(urls.map((url) => fetchSchemaRefUrl(url, fetchFn, appDefId, logger)))
	const successfulResults = fetchResults.filter((result) => result.success)
	if (successfulResults.length === 0) return {}
	const compSchemaRefResponses = successfulResults.map((res) => res.compSchemaRefResponse!)
	const mergedResponses = mergeComponentRefSchemas(compSchemaRefResponses)
	return fixMapNames(builderRefSchemasToRefSchemas(mergedResponses))
}

export const mergeRefs = (appDefIdToRefs: Record<string, RefSchemas>): RefSchemas =>
	Object.values(appDefIdToRefs).reduce((acc, refs) => {
		return Object.entries(refs).reduce((accMap, [mapName, mapRefs]) => {
			const existingMapRefs = accMap[mapName] || {}
			const mergedDataTypes = Object.entries(mapRefs).reduce((accDataTypes, [dataType, refInfoArray]) => {
				const existingRefInfoArray = existingMapRefs[dataType] || []
				return {
					...accDataTypes,
					[dataType]: [...existingRefInfoArray, ...refInfoArray],
				}
			}, existingMapRefs)

			return {
				...accMap,
				[mapName]: mergedDataTypes,
			}
		}, acc)
	}, {} as RefSchemas)

export function getBuilderComponentRefPromises(
	builderComponentsAppDefIds: Array<string>,
	clientSpecMap: ViewerClientSpecMap,
	fetchFn: FetchFn,
	logger?: Logger
): Array<{ appDefId: string; refsPromise: Promise<RefSchemas> }> {
	const appsToUrls = extractSchemaRefsUrls(builderComponentsAppDefIds, clientSpecMap, logger)
	return builderComponentsAppDefIds.map((appDefId) => {
		const appUrls = appsToUrls[appDefId] || []
		const refsPromise = appUrls.length > 0 ? getRefsForAppDefId(appDefId, appUrls, fetchFn, logger) : Promise.resolve({})
		return { appDefId, refsPromise }
	})
}

export const formatToAppDefIdToRefs = (
	results: Array<{ appDefId: string; refs: RefSchemas }>
): Record<string, RefSchemas> => {
	return results.reduce((acc, { appDefId, refs }) => {
		acc[appDefId] = refs
		return acc
	}, {} as Record<string, RefSchemas>)
}

export const getBuilderComponentRefSchemas = async (
	clientSpecMap: ViewerClientSpecMap,
	fetchFn: FetchFn,
	logger?: Logger,
): Promise<Record<string, RefSchemas>> => {
	const builderComponentsAppDefIds = getBuilderComponentsAppDefIds(clientSpecMap)
	const refPromises = getBuilderComponentRefPromises(builderComponentsAppDefIds, clientSpecMap, fetchFn, logger)

	const results = await Promise.all(
		refPromises.map(async ({ appDefId, refsPromise }) => ({ appDefId, refs: await refsPromise }))
	)

	return formatToAppDefIdToRefs(results)
}

export const formatBuilderRefSchemasRecord = (
	builderRefSchemasByAppDefId: Record<string, BuilderRefSchemas>
): Record<string, RefSchemas> => {
	return Object.entries(builderRefSchemasByAppDefId).reduce((acc, [appDefId, builderRefSchemas]) => {
		acc[appDefId] = fixMapNames(builderRefSchemasToRefSchemas(builderRefSchemas))
		return acc
	}, {} as Record<string, RefSchemas>)
}
